import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Copy, Check, Video, X, Plus, Folder, FolderOpen, FolderKanban, FolderClock } from 'lucide-react';
import { FolderCard } from '../../components/ui/folder-card';
import '../../styles/admin/VideoGallery.css';

export default function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null); // null = show folder view, number = show videos in that folder
  const [navigationPath, setNavigationPath] = useState([]); // breadcrumb navigation
  const [newGroupName, setNewGroupName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isManagerMode, setIsManagerMode] = useState(false); // Manager mode toggle
  const [selectedFolders, setSelectedFolders] = useState([]); // Selected folders for deletion
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [notification, setNotification] = useState({ isOpen: false, type: '', title: '', message: '' });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');
  const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB

  const showNotification = (type, title, message) => {
    setNotification({ isOpen: true, type, title, message });
    setTimeout(() => {
      setNotification({ isOpen: false, type: '', title: '', message: '' });
    }, 4000);
  };

  useEffect(() => {
    fetchGroups();
    fetchVideos();
  }, []);

  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
  });

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API_URL}/api/videos/groups/all`, { headers: authHeaders() });
      const data = await res.json();
      if (!data.success) return;
      const list = data.groups || [];
      if (list.length === 0) {
        await createDefaultGroup();
        return;
      }
      setGroups(list);
    } catch (e) {
      console.error('Error fetching groups', e);
    }
  };

  const createDefaultGroup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/videos/groups`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Default Folder' })
      });
      const data = await res.json();
      if (data.success) {
        setGroups([data.group]);
        setSelectedGroupId(data.group.id);
      }
    } catch (e) {
      console.error('Failed to create default folder', e);
    }
  };

  const fetchVideos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/videos`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setVideos(data.videos);
    } catch (e) {
      console.error('Error fetching videos', e);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
    } else if (file) {
      showNotification('error', 'Invalid File', 'Please select a valid video file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
    } else if (file) {
      showNotification('error', 'Invalid File', 'Please select a valid video file');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const uploadChunkedFile = async (file, groupId) => {
    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end, file.type || 'video/mp4');
      const chunkFile = new File([chunk], file.name, { type: file.type || 'video/mp4' });

      const formData = new FormData();
      formData.append('chunk', chunkFile, file.name);
      formData.append('chunkIndex', chunkIndex);
      formData.append('totalChunks', totalChunks);
      formData.append('fileName', file.name);
      formData.append('fileId', fileId);

      const response = await fetch(`${API_URL}/api/videos/upload-chunk`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Chunk upload failed: ${errText}`);
      }
      setUploadProgress(Math.round(((chunkIndex + 1) / totalChunks) * 100));
    }

    const completeResponse = await fetch(`${API_URL}/api/videos/complete-upload`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileId,
        fileName: file.name,
        totalChunks,
        fileSize: file.size,
        groupId
      })
    });
    const result = await completeResponse.json();
    if (!result.success) throw new Error('Failed to complete upload');
    return result.video;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showNotification('error', 'No File Selected', 'Please select a video file first');
      return;
    }
    if (!groups.length) {
      showNotification('error', 'No Folder Available', 'Please create a folder first');
      return;
    }
    
    // Determine target folder - use selected folder or first available
    let targetGroupId = selectedGroupId;
    if (!targetGroupId) {
      targetGroupId = groups[0]?.id;
      if (!targetGroupId) {
        showNotification('error', 'No Folder Selected', 'Please select a folder first');
        return;
      }
    }
    
    setUploading(true);
    setUploadProgress(0);
    try {
      await uploadChunkedFile(selectedFile, targetGroupId);
      await fetchVideos();
      setSelectedFile(null);
      setIsModalOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showNotification('success', 'Upload Complete', 'Video uploaded successfully!');
    } catch (error) {
      console.error('Error uploading video:', error);
      showNotification('error', 'Upload Failed', 'Failed to upload video. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteVideo = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Video',
      message: 'Type "confirm" to delete this video permanently',
      onConfirm: async (inputValue) => {
        if (inputValue?.toLowerCase() !== 'confirm') return false;
        
        try {
          const res = await fetch(`${API_URL}/api/videos/${id}`, {
            method: 'DELETE',
            headers: authHeaders()
          });
          const data = await res.json();
          if (data.success) {
            await fetchVideos();
            showNotification('success', 'Video Deleted', 'Video deleted successfully');
            return true;
          } else {
            showNotification('error', 'Delete Failed', 'Failed to delete video');
            return false;
          }
        } catch (e) {
          console.error(e);
          showNotification('error', 'Delete Failed', 'Failed to delete video');
          return false;
        }
      }
    });
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedId(url);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/videos/groups`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newGroupName.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setNewGroupName('');
        await fetchGroups();
        // Navigate to the newly created folder
        if (data.group?.id) {
          navigateToFolder(data.group.id, data.group.name);
        }
        showNotification('success', 'Folder Created', `Folder "${newGroupName.trim()}" created successfully`);
      } else {
        showNotification('error', 'Creation Failed', data.error || 'Failed to create folder');
      }
    } catch (e) {
      console.error(e);
      showNotification('error', 'Creation Failed', 'Failed to create folder');
    }
  };

  const quickCreateGroup = () => {
    setShowCreateModal(true);
  };

  const handleMoveVideo = async (videoId, groupId) => {
    try {
      const res = await fetch(`${API_URL}/api/videos/${videoId}/group`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupId })
      });
      const data = await res.json();
      if (data.success) {
        await fetchVideos();
        showNotification('success', 'Video Moved', 'Video moved to new folder successfully');
      } else {
        showNotification('error', 'Move Failed', data.error || 'Failed to move video');
      }
    } catch (e) {
      console.error(e);
      showNotification('error', 'Move Failed', 'Failed to move video');
    }
  };

  // Navigate into a folder (file explorer style)
  const navigateToFolder = (folderId, folderName) => {
    if (isManagerMode) return; // Prevent navigation in manager mode
    setNavigationPath(prev => [...prev, { id: folderId, name: folderName }]);
    setSelectedGroupId(folderId);
  };

  // Navigate back to folder view
  const navigateBack = () => {
    if (navigationPath.length > 0) {
      const newPath = [...navigationPath];
      newPath.pop();
      setNavigationPath(newPath);
      
      if (newPath.length === 0) {
        setSelectedGroupId(null); // Back to folder view
      } else {
        setSelectedGroupId(newPath[newPath.length - 1].id);
      }
    } else {
      setSelectedGroupId(null); // Back to folder view
    }
  };

  // Navigate to specific breadcrumb level
  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      // Navigate to root (folder view)
      setNavigationPath([]);
      setSelectedGroupId(null);
    } else {
      const newPath = navigationPath.slice(0, index + 1);
      setNavigationPath(newPath);
      setSelectedGroupId(newPath[newPath.length - 1].id);
    }
  };

  // Toggle manager mode
  const toggleManagerMode = () => {
    setIsManagerMode(!isManagerMode);
    setSelectedFolders([]);
    setSelectedVideoIds([]);
  };

  // Toggle folder selection
  const toggleFolderSelect = (folderId) => {
    setSelectedFolders(prev => 
      prev.includes(folderId) 
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  // Delete selected folders
  const handleDeleteSelectedFolders = async () => {
    if (selectedFolders.length === 0) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Folders',
      message: `Type "confirm" to delete ${selectedFolders.length} folder(s) permanently`,
      onConfirm: async (inputValue) => {
        if (inputValue?.toLowerCase() !== 'confirm') return false;

        try {
          for (const folderId of selectedFolders) {
            const res = await fetch(`${API_URL}/api/videos/groups/${folderId}`, {
              method: 'DELETE',
              headers: authHeaders()
            });
            const data = await res.json();
            if (!data.success) {
              showNotification('error', 'Delete Failed', `Failed to delete folder: ${data.error}`);
              return false;
            }
          }
          
          await fetchGroups();
          await fetchVideos();
          setSelectedFolders([]);
          showNotification('success', 'Folders Deleted', `Successfully deleted ${selectedFolders.length} folder(s)`);
          return true;
        } catch (error) {
          console.error('Error deleting folders:', error);
          showNotification('error', 'Delete Failed', 'Failed to delete folders');
          return false;
        }
      }
    });
  };

  const isYouTubeUrl = (url = '') => {
    try {
      const u = new URL(url);
      return ['youtube.com', 'www.youtube.com', 'youtu.be'].includes(u.hostname);
    } catch {
      return url.includes('youtube.com') || url.includes('youtu.be');
    }
  };

  const getYouTubeEmbed = (url = '') => {
    try {
      if (url.includes('youtube.com/watch')) {
        const u = new URL(url);
        const id = u.searchParams.get('v');
        const start = u.searchParams.get('t') || u.searchParams.get('start');
        return `https://www.youtube.com/embed/${id || ''}${start ? `?start=${start.replace('s', '')}` : ''}`;
      }
      if (url.includes('youtu.be/')) {
        const u = new URL(url);
        const id = u.pathname.replace('/', '');
        const start = u.searchParams.get('t') || u.searchParams.get('start');
        return `https://www.youtube.com/embed/${id}${start ? `?start=${start.replace('s', '')}` : ''}`;
      }
      return url;
    } catch {
      return url;
    }
  };

  const filteredVideos = videos.filter(v => v.group_id === selectedGroupId);
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);

  const toggleVideoSelect = (id) => {
    setSelectedVideoIds((prev) =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedVideoIds.length === 0) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Multiple Videos',
      message: `Type "confirm" to delete ${selectedVideoIds.length} video(s) permanently`,
      onConfirm: async (inputValue) => {
        if (inputValue?.toLowerCase() !== 'confirm') return false;
        
        try {
          for (const videoId of selectedVideoIds) {
            const res = await fetch(`${API_URL}/api/videos/${videoId}`, {
              method: 'DELETE',
              headers: authHeaders()
            });
            const data = await res.json();
            if (!data.success) {
              showNotification('error', 'Delete Failed', `Failed to delete video ${videoId}`);
              return false;
            }
          }
          
          await fetchVideos();
          setSelectedVideoIds([]);
          showNotification('success', 'Videos Deleted', `Successfully deleted ${selectedVideoIds.length} video(s)`);
          return true;
        } catch (error) {
          console.error('Error deleting videos:', error);
          showNotification('error', 'Delete Failed', 'Failed to delete videos');
          return false;
        }
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const groupMeta = groups.map((g) => {
    const itemsCount = videos.filter(v => v.group_id === g.id).length;
    const sizeLabel = `${itemsCount} item(s)`;
    const icon = <Folder className="h-8 w-8 text-gray-800" />;
    const variant = "default";
    return { ...g, sizeLabel, variant, icon };
  });

  return (
    <div className="video-gallery-container">
      <div className="video-gallery-header">
        <div>
          <h1>Video Gallery</h1>
          <p>Upload and manage your video content</p>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className={`manager-btn ${isManagerMode ? 'active' : ''}`}
            onClick={toggleManagerMode}
          >
            {isManagerMode ? 'Exit Manager' : 'Manager'}
          </button>
          <button className="upload-top-btn" onClick={quickCreateGroup}>
            <Plus size={18} />
            New Folder
          </button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      {(selectedGroupId !== null || navigationPath.length > 0) && (
        <div className="breadcrumb-nav" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginBottom: '1rem',
          padding: '8px 12px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <button 
            className="breadcrumb-item" 
            onClick={() => navigateToBreadcrumb(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#6366f1',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
          >
            <Folder size={16} style={{ marginRight: '4px', display: 'inline' }} />
            All Folders
          </button>
          {navigationPath.map((item, index) => (
            <React.Fragment key={item.id}>
              <span style={{ color: '#64748b' }}>/</span>
              <button 
                className="breadcrumb-item"
                onClick={() => navigateToBreadcrumb(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: index === navigationPath.length - 1 ? '#1e293b' : '#6366f1',
                  cursor: index === navigationPath.length - 1 ? 'default' : 'pointer',
                  textDecoration: index === navigationPath.length - 1 ? 'none' : 'underline',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontWeight: index === navigationPath.length - 1 ? '600' : '400'
                }}
              >
                <FolderOpen size={16} style={{ marginRight: '4px', display: 'inline' }} />
                {item.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Folder Grid - Only show when not inside a folder */}
      {selectedGroupId === null && (
        <>
          {/* Manager Mode Actions */}
          {isManagerMode && (selectedFolders.length > 0) && (
            <div className="manager-actions" style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '1rem',
              padding: '12px',
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              border: '1px solid #fecaca'
            }}>
              <span style={{ color: '#dc2626', fontWeight: '600' }}>
                {selectedFolders.length} folder(s) selected
              </span>
              <button 
                className="delete-selected-btn"
                onClick={handleDeleteSelectedFolders}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                <Trash2 size={16} style={{ marginRight: '4px' }} />
                Delete Selected
              </button>
            </div>
          )}
          
          <div className="folder-grid">
            {groupMeta.map((g) => (
              <div
                key={g.id}
                className="relative cursor-pointer"
                onClick={() => isManagerMode ? toggleFolderSelect(g.id) : navigateToFolder(g.id, g.name)}
              >
                {/* Manager Mode Checkbox */}
                {isManagerMode && (
                  <input
                    type="checkbox"
                    checked={selectedFolders.includes(g.id)}
                    onChange={() => toggleFolderSelect(g.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      width: '18px',
                      height: '18px',
                      zIndex: 2,
                      cursor: 'pointer'
                    }}
                  />
                )}
                <FolderCard
                  icon={g.icon}
                  title={g.name}
                  size={g.sizeLabel}
                  variant={g.variant}
                  className={`min-w-[100px] ${selectedFolders.includes(g.id) ? 'selected' : ''}`}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Videos Section - Only show when inside a folder */}
      {selectedGroupId !== null && (
        <div className="videos-section">
          <div className="videos-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button 
                className="back-btn"
                onClick={navigateBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#475569'
                }}
              >
                ← Back
              </button>
              <h2>Uploaded Videos ({filteredVideos.length})</h2>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="upload-top-btn" onClick={() => setIsModalOpen(true)}>
                <Upload size={16} />
                Upload to Folder
              </button>
              {selectedVideoIds.length > 0 && (
                <button className="upload-top-btn" onClick={handleDeleteSelected}>
                  <Trash2 size={16} />
                  Delete Selected
                </button>
              )}
            </div>
          </div>

          {filteredVideos.length === 0 ? (
            <div className="empty-state">
              <Video size={64} />
              <p>No videos in this folder yet</p>
            </div>
          ) : (
            <div className="videos-grid">
              {filteredVideos.map((video) => (
                <div key={video.id} className="video-card">
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <input
                      type="checkbox"
                      checked={selectedVideoIds.includes(video.id)}
                      onChange={() => toggleVideoSelect(video.id)}
                      style={{ 
                        display: isManagerMode ? 'block' : 'none',
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        width: '18px',
                        height: '18px',
                        zIndex: 2
                      }}
                    />
                  </div>
                  <div className="video-preview">
                    {isYouTubeUrl(video.previewUrl || video.fullUrl || video.url) ? (
                      <iframe
                        src={getYouTubeEmbed(video.previewUrl || video.fullUrl || video.url)}
                        title={video.original_name}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video controls>
                        <source src={video.previewUrl || video.fullUrl || video.url} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>

                  <div className="video-info">
                    <h4>{video.original_name}</h4>
                    <p className="video-size">{formatFileSize(video.file_size)}</p>
                    <p className="video-date">
                      {new Date(video.created_at).toLocaleDateString()}
                    </p>

                    <div className="video-group-select">
                      <label>Folder</label>
                      <select
                        value={video.group_id || selectedGroupId}
                        onChange={(e) => handleMoveVideo(video.id, Number(e.target.value))}
                      >
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="video-url">
                      <input
                        type="text"
                        value={video.fullUrl}
                        readOnly
                        onClick={(e) => e.target.select()}
                      />
                    </div>

                    <div className="video-actions">
                      <button
                        className="copy-btn"
                        onClick={() => handleCopyLink(video.fullUrl)}
                      >
                        {copiedId === video.fullUrl ? (
                          <>
                            <Check size={16} /> Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={16} /> Copy Link
                          </>
                        )}
                      </button>

                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteVideo(video.id)}
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {isModalOpen && (
        <div className="upload-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload Video</h3>
              <button className="icon-btn" onClick={() => setIsModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="group-select inline">
              <span>Folder</span>
              <select value={selectedGroupId || groups[0]?.id || ''} onChange={(e) => setSelectedGroupId(Number(e.target.value))}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div
              className={`dropzone ${isDragActive ? 'drag-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div className="dropzone-content">
                <Upload size={32} />
                <p>Drag & drop a video here, or click to browse</p>
                <span className="hint">Supports large files up to 2GB+</span>
              </div>
            </div>

            {selectedFile && (
              <div className="selected-file-row">
                <div>
                  <p className="file-name">{selectedFile.name}</p>
                  <span className="file-size">{formatFileSize(selectedFile.size)}</span>
                </div>
                <button
                  className="icon-btn"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p>{uploadProgress}% uploaded</p>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setIsModalOpen(false)} disabled={uploading}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                <Upload size={18} />
                {uploading ? 'Uploading...' : 'Upload Video'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateModal && (
        <div className="upload-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="create-folder-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Folder</h3>
              <button className="icon-btn" onClick={() => setShowCreateModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="group-create" style={{ width: '100%', marginTop: '12px' }}>
              <input
                type="text"
                placeholder="Folder name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </div>
            <div className="modal-actions" style={{ marginTop: '12px' }}>
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={async () => { await handleCreateGroup(); setShowCreateModal(false); }}>
                <Plus size={16} /> Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <ConfirmationModal
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={async (inputValue) => {
            const success = await confirmModal.onConfirm(inputValue);
            if (success) {
              setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
            }
          }}
          onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        />
      )}

      {/* Notification Popup */}
      {notification.isOpen && (
        <NotificationPopup
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification({ isOpen: false, type: '', title: '', message: '' })}
        />
      )}

    </div>
  );
}

// Notification Popup Component
function NotificationPopup({ type, title, message, onClose }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <Check size={24} />;
      case 'error':
        return <X size={24} />;
      default:
        return null;
    }
  };

  return (
    <div className={`notification-overlay ${isVisible ? 'visible' : ''}`}>
      <div className={`notification-popup ${type} ${isVisible ? 'slide-in' : 'slide-out'}`}>
        <div className="notification-content">
          <div className="notification-icon">
            {getIcon()}
          </div>
          <div className="notification-text">
            <h4>{title}</h4>
            <p>{message}</p>
          </div>
          <button className="notification-close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Confirmation Modal Component
function ConfirmationModal({ title, message, onConfirm, onCancel }) {
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    setIsProcessing(true);
    await onConfirm(inputValue);
    setIsProcessing(false);
  };

  return (
    <div className="confirm-modal-overlay">
      <div className="confirm-modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onCancel} disabled={isProcessing}>
            <X size={18} />
          </button>
        </div>
        
        <p>{message}</p>
        
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder='Type "confirm" to proceed'
          disabled={isProcessing}
          autoFocus
        />
        
        <div className="confirm-actions">
          <button 
            className="btn-secondary" 
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleConfirm}
            disabled={isProcessing || inputValue.toLowerCase() !== 'confirm'}
          >
            {isProcessing ? 'Processing...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
